export const USE_CASE_OPTIONS = [
  { value: 'generic_assistance_task', label: 'Generic assistance' },
  { value: 'restaurant_delivery', label: 'Restaurant delivery' },
  { value: 'pickup_errand', label: 'Pickup / errand' },
  { value: 'yard_cleanup', label: 'Yard cleanup' },
  { value: 'ride_help', label: 'Ride help' },
];

export const SOURCE_OPTIONS = [
  { value: 'manual_concierge', label: 'Manual concierge' },
  { value: 'facebook_dm', label: 'Facebook DM' },
  { value: 'hip_camden', label: 'Hip Camden' },
  { value: 'down_home', label: 'Down Home' },
  { value: 'restaurant_referral', label: 'Restaurant referral' },
  { value: 'business_outreach', label: 'Business outreach' },
  { value: 'phone_call', label: 'Phone call' },
];

export const USE_CASE_PRESETS = {
  generic_assistance_task: {
    category: 'Errands',
    taskType: 'errand',
    title: 'Local assistance task',
    description: 'Help with a local task.',
    requesterFacingNotes: 'Review the task details before funding.',
  },
  restaurant_delivery: {
    category: 'Delivery',
    taskType: 'delivery',
    title: 'Restaurant delivery',
    description: 'Pick up a food order and deliver it locally.',
    requesterFacingNotes: 'Confirm pickup time before funding.',
  },
  pickup_errand: {
    category: 'Pickup / errand',
    taskType: 'errand',
    title: 'Local pickup',
    description: 'Pick up a local item and deliver it to the requester.',
    requesterFacingNotes: 'Confirm item is ready before funding.',
  },
  yard_cleanup: {
    category: 'Yard / cleanup',
    taskType: 'errand',
    title: 'Yard cleanup help',
    description: 'Help with a local cleanup task.',
    requesterFacingNotes: 'Requester should confirm tools/details before funding.',
  },
  ride_help: {
    category: 'Ride / transport',
    taskType: 'assisted_transport',
    title: 'Local ride help',
    description: 'Help with local transportation.',
    requesterFacingNotes: 'Confirm pickup, destination, and timing before funding.',
  },
};

export const CONCIERGE_PATTERNS = [
  {
    key: 'down_home_delivery',
    name: 'Down Home delivery',
    summary: 'Restaurant/customer delivery with a Down Home pickup label.',
    source: 'down_home',
    src: 'down_home',
    sourceLabel: 'Down Home delivery',
    useCase: 'restaurant_delivery',
    category: 'Delivery',
    taskType: 'delivery',
    title: 'Delivery from Down Home',
    description:
      'Pick up a paid food order from Down Home and deliver it to the customer.',
    requesterFacingNotes:
      'Review the pickup and delivery details before funding. Once funded, local helpers can accept the task.',
    internalNotes:
      'Restaurant/customer delivery draft. Confirm the order is already placed or clarify ordering/payment before sending.',
    pickupAddress: 'Down Home',
    pricingRule: { type: 'per_mile', rate: 2, round: 'ceil_dollar' },
  },
  {
    key: 'walmart_pickup',
    name: 'Walmart pickup',
    summary: 'Local pickup/errand pattern with a manual pickup confirmation.',
    source: 'manual_concierge',
    src: 'manual_concierge',
    sourceLabel: 'Walmart pickup',
    useCase: 'pickup_errand',
    category: 'Pickup / errand',
    taskType: 'errand',
    title: 'Walmart pickup',
    description: 'Pick up a local order from Walmart and deliver it to the requester.',
    requesterFacingNotes:
      'Confirm the item is ready and review delivery details before funding.',
    internalNotes:
      'Confirm pickup readiness, item size, and whether the requester already paid.',
    pickupAddress: 'Walmart',
    pricingRule: { type: 'per_mile', rate: 2, round: 'ceil_dollar' },
  },
  {
    key: 'food_truck_delivery',
    name: 'Food truck delivery',
    summary: 'Food truck/customer delivery with label-only pickup.',
    source: 'restaurant_referral',
    src: 'restaurant_referral',
    sourceLabel: 'Food truck delivery',
    useCase: 'restaurant_delivery',
    category: 'Delivery',
    taskType: 'delivery',
    title: 'Food truck delivery',
    description:
      'Pick up a paid food order from the local food truck area and deliver it to the customer.',
    requesterFacingNotes:
      'Review the pickup location and delivery details before funding.',
    internalNotes:
      'Confirm the food truck location and whether the order is already paid.',
    pickupAddress: 'Food truck area',
    pricingRule: { type: 'per_mile', rate: 2, round: 'ceil_dollar' },
  },
  {
    key: 'generic_errand',
    name: 'Generic errand',
    summary: 'General pickup, drop-off, or small local help request.',
    source: 'manual_concierge',
    src: 'manual_concierge',
    sourceLabel: 'Generic errand',
    useCase: 'pickup_errand',
    category: 'Errands',
    taskType: 'errand',
    title: 'Local errand help',
    description: 'Help with a local pickup, drop-off, or errand.',
    requesterFacingNotes: 'Review the errand details before funding.',
    internalNotes: 'Clarify timing, pickup readiness, and item size before sending.',
    pricingRule: { type: 'per_mile', rate: 2, round: 'ceil_dollar' },
  },
  {
    key: 'yard_cleanup_lead',
    name: 'Yard / cleanup lead',
    summary: 'Service-address help where the operator should set pay manually.',
    source: 'manual_concierge',
    src: 'manual_concierge',
    sourceLabel: 'Yard cleanup lead',
    useCase: 'yard_cleanup',
    category: 'Yard / cleanup',
    taskType: 'errand',
    title: 'Yard cleanup help',
    description: 'Help with a local yard or cleanup task.',
    requesterFacingNotes:
      'Review the cleanup scope, photos, and timing before funding.',
    internalNotes:
      'Ask for photos, scope, tools needed, debris handling, and preferred timing.',
    pricingRule: { type: 'manual' },
  },
  {
    key: 'custom_assistance_draft',
    name: 'Custom assistance draft',
    summary: 'Start with the generic assistance defaults.',
    source: 'manual_concierge',
    src: 'manual_concierge',
    sourceLabel: '',
    useCase: 'generic_assistance_task',
    category: 'Errands',
    taskType: 'errand',
    title: '',
    description: '',
    requesterFacingNotes: '',
    internalNotes: '',
    pricingRule: { type: 'manual' },
  },
];

export const LOCAL_PLACE_SUGGESTIONS = [
  {
    id: 'camden:down-home',
    regionSlug: 'camden',
    name: 'Down Home',
    aliases: ['down home', 'downhome', 'down home restaurant'],
    placeKind: 'restaurant',
    privacyLevel: 'public_business',
    addressStatus: 'label_only',
  },
  {
    id: 'camden:walmart',
    regionSlug: 'camden',
    name: 'Walmart',
    aliases: ['walmart', 'wal-mart'],
    placeKind: 'store',
    privacyLevel: 'public_business',
    addressStatus: 'label_only',
  },
  {
    id: 'camden:food-truck-area',
    regionSlug: 'camden',
    name: 'Food truck area',
    aliases: ['food truck', 'food trucks', 'food truck area'],
    placeKind: 'food_truck_area',
    privacyLevel: 'public_place',
    addressStatus: 'label_only',
  },
  {
    id: 'camden:collision-specialist',
    regionSlug: 'camden',
    name: 'Collision Specialist',
    aliases: ['collision specialist', 'collision specialists'],
    placeKind: 'repair_shop',
    privacyLevel: 'public_business',
    publicAddress: '134 West Main Street, Camden, TN 38320',
    coordinates: { lat: 36.058, lon: -88.096 },
    addressStatus: 'confirmed_public',
  },
  {
    id: 'camden:benton-county-courthouse',
    regionSlug: 'camden',
    name: 'Benton County Courthouse',
    aliases: ['courthouse', 'benton county courthouse'],
    placeKind: 'public_landmark',
    privacyLevel: 'public_place',
    addressStatus: 'label_only',
  },
];

export const SCRIPT_CARDS = [
  {
    title: 'Facebook Comment',
    text:
      "Hey - this is exactly the kind of thing JG LocalOps is built for. If you want, I can help turn this into a paid local task so nearby helpers can see it and accept it. It only takes a couple minutes. I'd just need the best email to send you a prefilled link. You can review it first and only fund it if it looks right.",
  },
  {
    title: 'Facebook DM',
    text:
      "Hey, I saw your post about needing help with [task type]. This should be a good fit for JG LocalOps. We can turn it into a paid local task and notify nearby Camden helpers who are looking to earn. I can make it easy: send me the best email, and I'll send you a prefilled link. You can review the task details, adjust anything, and only fund it if it looks right.",
  },
  {
    title: 'Phone/In-Person',
    text:
      "I can help you set that up as a local paid task. The way it works is simple: I'll send you a prefilled link by email. You open it, review the task, make sure the details and price look right, then fund it if you want to post it. Once it's funded, local helpers can be notified and someone can accept it. What's the best email to send that to?",
  },
  {
    title: 'Restaurant/Customer Delivery',
    text:
      "If someone calls in an order and asks about delivery, you can send them to us. We can help them post a local delivery task so a Camden helper can pick it up and bring it to them. For now, the easiest way is: get their email or have them message us. We'll send them a prefilled link, they review it, fund it if it looks right, and local helpers get notified.",
  },
  {
    title: 'Email Hesitation',
    text:
      "No problem. The email is just how we send you the secure task link so you can review it yourself. You're not agreeing to anything by giving the email. You only move forward if the task looks right to you.",
  },
  {
    title: 'Payment Hesitation',
    text:
      "No. First you review the task details. If it looks right, then you fund the task so local helpers know it's real and can accept it. If it doesn't look right, you don't have to post it.",
  },
  {
    title: 'Who Does The Work',
    text:
      'Local people who use JG LocalOps to earn money by completing tasks. Once the task is funded, nearby helpers can be notified and someone can accept it through the platform.',
  },
  {
    title: 'DoorDash Comparison',
    text:
      "Not exactly. It's broader and more local. It can be delivery, pickup, errands, ride help, yard help, small jobs - whatever local task someone needs done. The idea is Camden people helping Camden people.",
  },
  {
    title: 'Nobody Accepts',
    text:
      "You're not stuck. We can share it again, notify helpers again, or raise the pay if needed. The goal is to help the task clear locally.",
  },
  {
    title: 'Safety/Scam',
    text:
      'The task is handled through the platform. Helpers should not ask you to send money privately, buy gift cards, or pay outside the task flow. You review and fund the task yourself, and the helper is paid after the work is completed and verified.',
  },
  {
    title: 'Short Close',
    text: "Send me the best email and I'll get the prefilled task link over to you.",
  },
  {
    title: 'Soft Close',
    text: 'I can send the link over and you can decide after you see it. What email should I use?',
  },
];

export const trim = (value) => String(value || '').trim();

export const getUseCasePreset = (useCase) =>
  USE_CASE_PRESETS[useCase] || USE_CASE_PRESETS.generic_assistance_task;

export const getConciergePattern = (patternKey) =>
  CONCIERGE_PATTERNS.find((pattern) => pattern.key === patternKey) ||
  CONCIERGE_PATTERNS.find((pattern) => pattern.key === 'custom_assistance_draft');

export const getDynamicLabels = (useCase) => {
  if (useCase === 'restaurant_delivery') {
    return {
      pickup: 'Pickup address / restaurant',
      destination: 'Customer delivery address',
      pay: 'Suggested task pay',
    };
  }
  if (useCase === 'pickup_errand') {
    return {
      pickup: 'Pickup address / store',
      destination: 'Drop-off address',
      pay: 'Suggested task pay',
    };
  }
  if (useCase === 'ride_help') {
    return {
      pickup: 'Pickup address',
      destination: 'Destination address',
      pay: 'Suggested task pay',
    };
  }
  if (useCase === 'yard_cleanup') {
    return {
      pickup: 'Service address',
      destination: 'Destination, if different',
      pay: 'Suggested task pay',
    };
  }
  return {
    pickup: 'Task location / where help starts',
    destination: 'Destination, if different',
    pay: 'Suggested task pay',
  };
};

const getPricingRuleLabel = (rule = {}) => {
  if (rule.type === 'per_mile') {
    return `$${Number(rule.rate || 0).toFixed(2)} / mile when route data is confirmed`;
  }
  if (rule.type === 'fixed') {
    return `Fixed operator suggestion: $${Number(rule.amount || 0).toFixed(2)}`;
  }
  return 'Manual operator estimate';
};

export const getPriceSuggestion = (form) => {
  const pattern = getConciergePattern(form.patternKey);
  const rule = pattern?.pricingRule || { type: 'manual' };
  const hasPickup = Boolean(trim(form.pickupAddress));
  const hasDestination = Boolean(trim(form.deliveryAddress));
  const routeLabel = `${trim(form.pickupAddress) || 'Pickup not set'} -> ${
    trim(form.deliveryAddress) || 'destination not set'
  }`;

  if (rule.type === 'fixed') {
    return {
      value: String(rule.amount),
      status: 'suggested',
      label: `Suggested task pay is available from the selected pattern. ${getPricingRuleLabel(rule)}.`,
      routeLabel,
      pricingRuleLabel: getPricingRuleLabel(rule),
    };
  }

  if (rule.type === 'per_mile') {
    return {
      value: '',
      status: hasPickup && hasDestination ? 'manual_route_unavailable' : 'needs_addresses',
      label: hasPickup && hasDestination
        ? 'Route distance is not confirmed in v1. Enter suggested task pay manually or use a confirmed quote.'
        : 'Add confirmed pickup and destination addresses to estimate suggested pay.',
      routeLabel,
      pricingRuleLabel: getPricingRuleLabel(rule),
    };
  }

  if (form.useCase === 'yard_cleanup') {
    return {
      value: '',
      status: 'manual',
      label: 'Yard cleanup pricing should stay operator-entered.',
      routeLabel,
      pricingRuleLabel: getPricingRuleLabel(rule),
    };
  }

  return {
    value: '',
    status: 'manual',
    label: 'Enter the operator estimate the requester should review before funding.',
    routeLabel,
    pricingRuleLabel: getPricingRuleLabel(rule),
  };
};

export const getPlaceSuggestions = (value, regionSlug = 'camden') => {
  const query = trim(value).toLowerCase();
  if (!query) return [];
  return LOCAL_PLACE_SUGGESTIONS.filter((place) => {
    if (place.regionSlug !== regionSlug) return false;
    const values = [place.name, ...(place.aliases || [])].map((item) =>
      String(item || '').toLowerCase(),
    );
    return values.some((item) => item.includes(query) || query.includes(item));
  }).slice(0, 4);
};

export const buildDefaultConciergeForm = () => ({
  patternKey: 'custom_assistance_draft',
  requesterEmail: '',
  requesterName: '',
  requesterPhone: '',
  source: 'manual_concierge',
  src: 'manual_concierge',
  sourceLabel: '',
  useCase: 'generic_assistance_task',
  category: USE_CASE_PRESETS.generic_assistance_task.category,
  taskType: USE_CASE_PRESETS.generic_assistance_task.taskType,
  title: '',
  description: '',
  pickupAddress: '',
  deliveryAddress: '',
  suggestedPriceUsd: '',
  requesterFacingNotes: '',
  internalNotes: '',
  originalRequest: '',
});

export const applyPatternToForm = (form, patternKey, manualFields = new Set()) => {
  const pattern = getConciergePattern(patternKey);
  const next = {
    ...form,
    patternKey: pattern.key,
    source: pattern.source,
    src: pattern.src || pattern.source,
    sourceLabel: pattern.sourceLabel || '',
    useCase: pattern.useCase,
    category: pattern.category,
    taskType: pattern.taskType,
  };

  ['title', 'description', 'requesterFacingNotes', 'internalNotes', 'pickupAddress'].forEach(
    (field) => {
      if (!manualFields.has(field)) {
        next[field] = pattern[field] || '';
      }
    },
  );

  if (!manualFields.has('suggestedPriceUsd')) {
    const suggestion = getPriceSuggestion(next);
    next.suggestedPriceUsd = suggestion.value || '';
  }

  return next;
};

export const buildDraftPayload = (form) => ({
  requesterEmail: form.requesterEmail,
  requesterName: form.requesterName,
  requesterPhone: form.requesterPhone,
  source: form.source,
  src: form.src,
  sourceLabel: form.sourceLabel,
  useCase: form.useCase,
  category: form.category,
  taskType: form.taskType,
  title: form.title,
  description: form.description,
  pickupAddress: form.pickupAddress,
  deliveryAddress: form.deliveryAddress,
  suggestedPriceUsd: Number(form.suggestedPriceUsd),
  requesterFacingNotes: form.requesterFacingNotes,
  internalNotes: form.internalNotes,
  regionSlug: 'camden',
  marketSlug: 'camden',
  intent: 'post_task',
});

export const getRelevantScriptTitles = (form) => {
  const titles = new Set();
  if (!trim(form.requesterEmail)) {
    titles.add('Short Close');
    titles.add('Soft Close');
    titles.add('Email Hesitation');
  }
  if (form.source === 'facebook_dm') {
    titles.add('Facebook Comment');
    titles.add('Facebook DM');
  }
  if (form.useCase === 'restaurant_delivery') {
    titles.add('Restaurant/Customer Delivery');
  }
  if (!(Number(form.suggestedPriceUsd) > 0)) titles.add('Payment Hesitation');
  if (/scam|safe|safety|gift card|outside/i.test(form.originalRequest)) {
    titles.add('Safety/Scam');
  }
  return titles;
};
